import React, { useEffect } from "react";
import {
  Vector3,
  Vector2,
  Raycaster,
  Matrix4,
  Ray,
  Euler,
  DataTexture,
  BufferGeometry,
  Uint16Attribute,
  Float32Attribute,
  Color,
  ShaderMaterial,
  AdditiveBlending,
  RGBAFormat,
  FloatType,
  TextureLoader,
  LineSegments,
  Points,
  Box3,
  Quaternion
} from "three";
import {SpherePaths} from "./spherePaths.js";

import Stats from "three/examples/js/libs/stats.min.js";
import { fragmentShader } from "./points.fragment.shader.js";
import { vertexShader } from "./points.vertex.shader.js";
import { lineNetworkFragmentShader } from "./line_network.fragment.shader.js";
import { lineNetworkVertexShader } from "./line_network.vertex.shader.js";
import {
  graphNodeIDs,
  graphNodePosXY,
  graphEdgesSrcTargetIDs,
} from "./graphData.js";
import Pages from "./pages/Pages.js";
import {
  forceSimulation,
  forceManyBody,
  forceCenter,
  forceLink,
  forceCollide,
  forceX,
  forceY,
} from "d3";

let vizMode = 0.0;

let points,
line,
topoLines,
objLines,
animationFrameStep,
stats,
raycaster,
mouse,
inverseMatrix,
ray


function createAnimationsOptimized(){
  //for the sphere blobs in topography mode:
  //we build gpgpu data textures for the points.vertex shader to animate paths on the GPU
  //(generated in utils/generateSpherePaths from data found in the original createAnimations method) 
  //
  let textures = {};
  Object.keys(SpherePaths).forEach(pathID=>{
    const {textureSize,path,items} = SpherePaths[pathID];
    const uvPos = new Float32Array(textureSize * textureSize * 4);
    uvPos.set(path)
    for(let i=0;i<path.length;i+=4){
      uvPos[i+0] = path[i+0];
      uvPos[i+1] = path[i+1];
      uvPos[i+2] = path[i+2];
      uvPos[i+3] = path[i+3];
    }
    const dataTexture = new DataTexture(
      uvPos,
      textureSize,
      textureSize,
      RGBAFormat,
      FloatType
    );
    dataTexture.needsUpdate = true;
    textures[pathID] = {
      texture:dataTexture,
      dims:textureSize,
      size:items
    }
  })
  return textures;
}





function ContourPoints(props) {
  useEffect(() => {
    const {data, generateGraphData, scene, renderer, camera, showStats} = props

    if (!(scene && renderer && camera)) {
      return
    }
    let performHeightAnimation = false;
    const positions = [];
    const uv = [];
    const colors = [];
    const sizes = [];
    const uvPos = new Float32Array(256 * 256 * 4);
    const siblingsStart = [];
    const siblingsEnd = [];
    const myIndex = [];
    mouse = new Vector2();
    raycaster = new Raycaster();
    inverseMatrix = new Matrix4();
    ray = new Ray();
    raycaster.params.Points.threshold = 10;
    let globalPointI = 0;
    //mode0 params
    let mode0RadiusMax = 10;
    let mode0RadiusMin = -100;
    let mode0CentroidMinX = -100;
    let mode0CentroidMinY = -100;
    let mode0CentroidMaxX = 150;
    let mode0CentroidMaxY = 150;
    let mode0SpeedMax = 2.0;

    let mode0PosInit = [];
    let mode0Radius = [];
    let mode0Centroid = [];
    let mode0Speed = [];
    let mode0DeltaStart = [];

    let myIndexLINES = [];
    let mode0PosInitLINES = [];
    let mode0RadiusLINES = [];
    let mode0CentroidLINES = [];
    let mode0SpeedLINES = [];
    let mode0DeltaStartLINES = [];

    let mode2PosLINES = [];

    let mode1CommunityID = [];
    let mode1CommunityIndex = {};
    let mode1Positions = [];
    let mode1IsNode = [];
    let mode1IsEdgeNode = [];
    let mode1ID = [];
    let totalLength = 0;

    let indices = [];
    let linepositions = [];
    let linecolors = [];
    let linedisplacement = [];

    //get total length of points
    data.map((multipoly) => {
      if (multipoly.value <= 0) {
        return false;
      }
      multipoly.coordinates.map((parts) => {
        parts.map((points) => {
          points.map((point) => {
            totalLength += 1;
            return false;
          });
          return false;
        });
        return false;
      });
      return false;
    });

    data.map((multipoly, i) => {
      if (multipoly.value <= 0) {
        return false;
      }
      const z = parseFloat(i * 0.5); //multipoly.value;
      multipoly.coordinates.map((parts) => {
        parts.map((points) => {
          let startI = globalPointI;
          let endI = globalPointI + points.length;
          points.map((point, pointI) => {
            myIndex.push(globalPointI);
            siblingsStart.push(startI);
            siblingsEnd.push(endI);
            const uvx = Math.floor(globalPointI % 256);
            const uvy = Math.floor(globalPointI / 256);
            uv.push(uvx, uvy);
            let x = parseFloat(point[0]);
            let y = parseFloat(-point[1]);
            //pointI % 2 && positions.push(x, y, z);
            positions.push(x, y, z); //pointI % 2 && .... cuts out half of the points from the graph
            //if we want less points during mode 0, cut half in the points shader

            uvPos[globalPointI * 4 + 0] = x;
            uvPos[globalPointI * 4 + 1] = y;
            uvPos[globalPointI * 4 + 2] = z;
            uvPos[globalPointI * 4 + 3] = 0.0;
            globalPointI += 1;

            sizes.push(1.0);
            colors.push(
              0.03137254901960784,
              0.9137254901960784,
              0.7372549019607844
            );

            let m0Radius = Math.random() * mode0RadiusMax + mode0RadiusMin;
            let m0CentroidX =
              Math.random() * mode0CentroidMaxX + mode0CentroidMinX;
            let m0CentroidY =
              Math.random() * mode0CentroidMaxY + mode0CentroidMinY;
            let m0Speed = Math.random() * mode0SpeedMax;
            let m0DeltaStart = Math.random() * (Math.PI * 2);
            let m0InitX = m0CentroidX + Math.sin(m0DeltaStart) * m0Radius;
            let m0InitY = m0CentroidY + Math.cos(m0DeltaStart) * m0Radius;

            mode0PosInit.push(m0InitX, m0InitY, 0);
            mode0Radius.push(m0Radius);
            mode0Centroid.push(m0CentroidX, m0CentroidY, 0);
            mode0Speed.push(m0Speed);
            mode0DeltaStart.push(m0DeltaStart);

            if (globalPointI % 10 === 0) {
              //is going to be part of mode1

              let communityID = Math.floor(
                (globalPointI / totalLength) * 15.99
              );
              if (typeof mode1CommunityIndex[communityID] === "undefined") {
                mode1CommunityIndex[communityID] = { nodes: [], edges: [] };
              }
              mode1CommunityID.push(communityID);
              mode1ID.push(globalPointI);
              mode1CommunityIndex[communityID].nodes.push({
                communityID,
                id: globalPointI,
                x: 0,
                y: 0,
                z: 0,
                size: 1.0,
              });

              mode1IsEdgeNode.push(0.0);
            } else {
              mode1IsEdgeNode.push(0.0);
              mode1CommunityID.push(-1.0);
              mode1ID.push(globalPointI);
            }
            return false;
          });
          return false;
        });
        return false;
      });
      return false;
    });
    let graphNodes = [];
    let graphEdges = [];
    Object.keys(mode1CommunityIndex).map((communityID) => {
      let commData = mode1CommunityIndex[communityID];
      let nodes = commData.nodes;
      let edges = commData.edges;
      nodes.map((node, i) => {
        const neighbors = Math.floor(Math.random() * 5);
        let neighborIndex = {};
        let iter = 0;
        let tests = [];
        if (i % 5 !== 0) {
          return false;
        }

        while (Object.keys(neighborIndex).length <= neighbors) {
          let cand = Math.floor(Math.random() * (nodes.length * 0.99));
          tests.push(cand);
          if (!neighborIndex[cand] && cand !== i) {
            neighborIndex[cand] = true;
            edges.push({ source: node, target: nodes[cand] });
          }
          iter += 1;
          if (iter >= 100) {
            break;
          }
        }
        return false;
      });
      graphNodes = graphNodes.concat(nodes);
      graphEdges = graphEdges.concat(edges);
      return false;
    });

    let xCenter = {};
    let yCenter = {};
    let gridW = 1; //one big cluster boo
    //let gridW = 4; //grid of 16 clusters yay

    Object.keys(mode1CommunityIndex).map((communityID, i) => {
      let x = i % gridW;
      let y = Math.floor(i / gridW);
      /*

      //good stuff: grid of 4*4
      xCenter[communityID] = x * 950;
      yCenter[communityID] = y * 950;*/

      //superbasic: grid 4*4 smooshed to group of 1
      /*xCenter[communityID] = x * 100;
      yCenter[communityID] = y * 100;*/
      //super super basic
      xCenter[communityID] = x * 11;
      yCenter[communityID] = y * 11;

      //radial arrangements
      /*let radian = Math.PI*2 * (i/Object.keys(mode1CommunityIndex).length);
      xCenter[communityID] = Math.sin(radian) * 4325;
      yCenter[communityID] = Math.cos(radian) * 4325;*/

      //radial arrangements = orange segments
      /*let radian = Math.PI*2 * (i/Object.keys(mode1CommunityIndex).length);
      xCenter[communityID] = Math.sin(radian) * 325;
      yCenter[communityID] = Math.cos(radian) * 325;*/
      return false;
    });

    let index = {};
    for (let i = 0; i < totalLength; i++) {
      let pxDefX = Math.random() * 3000 + -3000;
      let pxDefY = Math.random() * 3000 + -3000;
      let pxDefZ = Math.random() * 3000 + -3000;
      index[i] = { pos: [pxDefX, pxDefY, pxDefZ], isNode: 0 };
    }
    let cameraTarget = new Vector3(0, 0, 0);
    /*if(cameraTarget.boundingSphere){
      cameraTarget = cameraTarget.boundingSphere.center;
    }
    else{
      console.log("NO CAMERATARGET SET");
    }*/
    let avgX = 0;
    let avgY = 0;
    let avgZ = 0;
    let avgSize = 0;
    let minY = Infinity;
    let minZ = Infinity;
    let maxY = -Infinity;
    let maxZ = -Infinity;
    //let lastNode;
    if (generateGraphData) {
      const simulation = forceSimulation(graphNodes)
        .force("charge", forceManyBody().strength(-900))
        .force("center", forceCenter(0, 0))
        .force(
          "collision",
          forceCollide().radius(function (d) {
            return d.size * 50;
          })
        )
        .force(
          "x",
          forceX()
            .x(function (d) {
              return xCenter[d.communityID];
            })
            .strength(0.14)
        )
        .force(
          "y",
          forceY()
            .y(function (d) {
              return yCenter[d.communityID];
            })
            .strength(0.14)
        )
        .force("link", forceLink().links(graphEdges).distance(20).strength(2));
      simulation.tick(100);

      let simIDs = [];
      let simPos = [];
      simulation.nodes().map((node, i) => {
        let id = node.id;
        avgX += 0;
        avgY += node.x * 0.1;
        avgZ += node.y * 0.1;
        avgSize += 1;
        minY = Math.min(node.x * 0.1, minY);
        minZ = Math.min(node.y * 0.1, minZ);
        maxY = Math.max(node.x * 0.1, maxY);
        maxZ = Math.max(node.y * 0.1, maxZ);
        index[id] = {
          pos: [node.z * 0, node.x * 0.1, node.y * 0.1],
          isNode: 1,
        };
        simIDs.push(id);
        simPos.push(node.x * 0.1, node.y * 0.1);
        return false;
      });
      //sim node ids export
      let graphSourceTargetIDs = [];
      graphEdges.map((edge, i) => {
        graphSourceTargetIDs.push(edge.source.id, edge.target.id);
        linepositions.push(0, edge.source.x * 0.1, edge.source.y * 0.1);
        linecolors.push(
          0.03137254901960784,
          0.9137254901960784,
          0.7372549019607844
        );
        linedisplacement.push(0, 0, 0);

        linepositions.push(0, edge.target.x * 0.1, edge.target.y * 0.1);
        linecolors.push(
          0.03137254901960784,
          0.9137254901960784,
          0.7372549019607844
        );
        linedisplacement.push(0, 0, 0);

        mode1IsEdgeNode[edge.source.id] = 1.0;
        mode1IsEdgeNode[edge.target.id] = 1.0;
        indices.push(i * 2, i * 2 + 1);

        myIndexLINES.push(edge.source.id, edge.target.id);
        mode0PosInitLINES.push(
          mode0PosInit[edge.source.id * 3 + 0],
          mode0PosInit[edge.source.id * 3 + 1],
          mode0PosInit[edge.source.id * 3 + 2],
          mode0PosInit[edge.target.id * 3 + 0],
          mode0PosInit[edge.target.id * 3 + 1],
          mode0PosInit[edge.target.id * 3 + 2]
        );
        mode0RadiusLINES.push(
          mode0Radius[edge.source.id * 1 + 0],
          mode0Radius[edge.target.id * 1 + 0]
        );

        mode0CentroidLINES.push(
          mode0Centroid[edge.source.id * 3 + 0],
          mode0Centroid[edge.source.id * 3 + 1],
          mode0Centroid[edge.source.id * 3 + 2],
          mode0Centroid[edge.target.id * 3 + 0],
          mode0Centroid[edge.target.id * 3 + 1],
          mode0Centroid[edge.target.id * 3 + 2]
        );

        mode0SpeedLINES.push(
          mode0Speed[edge.source.id * 1 + 0],
          mode0Speed[edge.target.id * 1 + 0]
        );

        mode0DeltaStartLINES.push(
          mode0DeltaStart[edge.source.id * 1 + 0],
          mode0DeltaStart[edge.target.id * 1 + 0]
        );

        mode2PosLINES.push(
          uvPos[edge.source.id * 4 + 0],
          uvPos[edge.source.id * 4 + 1],
          uvPos[edge.source.id * 4 + 2],
          uvPos[edge.target.id * 4 + 0],
          uvPos[edge.target.id * 4 + 1],
          uvPos[edge.target.id * 4 + 2]
        );

        return false;
      });
    } else {
      //use static data and save cpu cycles on init
      graphNodeIDs.map((id, i) => {
        const x = graphNodePosXY[i * 2 + 0];
        const y = graphNodePosXY[i * 2 + 1];

        avgX += 0;
        avgY += x * 0.1;
        avgZ += y * 0.1;
        avgSize += 1;
        minY = Math.min(x * 0.1, minY);
        minZ = Math.min(y * 0.1, minZ);
        maxY = Math.max(x * 0.1, maxY);
        maxZ = Math.max(y * 0.1, maxZ);
        index[id] = { pos: [0, x * 0.1, y * 0.1], isNode: 1 };
        return false;
      });
      for (let i = 0; i <= graphEdgesSrcTargetIDs.length - 2; i += 2) {
        const sourceID = graphEdgesSrcTargetIDs[i];
        const targetID = graphEdgesSrcTargetIDs[i + 1];

        const sourceX = index[sourceID].pos[1];
        const sourceY = index[sourceID].pos[2];
        const targetX = index[targetID].pos[1];
        const targetY = index[targetID].pos[2];
        linepositions.push(0, sourceX, sourceY);
        linecolors.push(
          0.03137254901960784,
          0.9137254901960784,
          0.7372549019607844
        );
        linedisplacement.push(0, 0, 0);

        linepositions.push(0, targetX, targetY);
        //linecolors.push(0.35,0.35,0.35);
        linecolors.push(
          0.03137254901960784,
          0.9137254901960784,
          0.7372549019607844
        );
        linedisplacement.push(0, 0, 0);

        mode1IsEdgeNode[sourceID] = 1.0;
        mode1IsEdgeNode[targetID] = 1.0;
        indices.push(i, i + 1);

        myIndexLINES.push(sourceID, targetID);
        mode0PosInitLINES.push(
          mode0PosInit[sourceID * 3 + 0],
          mode0PosInit[sourceID * 3 + 1],
          mode0PosInit[sourceID * 3 + 2],
          mode0PosInit[targetID * 3 + 0],
          mode0PosInit[targetID * 3 + 1],
          mode0PosInit[targetID * 3 + 2]
        );
        mode0RadiusLINES.push(
          mode0Radius[sourceID * 1 + 0],
          mode0Radius[targetID * 1 + 0]
        );

        mode0CentroidLINES.push(
          mode0Centroid[sourceID * 3 + 0],
          mode0Centroid[sourceID * 3 + 1],
          mode0Centroid[sourceID * 3 + 2],
          mode0Centroid[targetID * 3 + 0],
          mode0Centroid[targetID * 3 + 1],
          mode0Centroid[targetID * 3 + 2]
        );

        mode0SpeedLINES.push(
          mode0Speed[sourceID * 1 + 0],
          mode0Speed[targetID * 1 + 0]
        );

        mode0DeltaStartLINES.push(
          mode0DeltaStart[sourceID * 1 + 0],
          mode0DeltaStart[targetID * 1 + 0]
        );

        mode2PosLINES.push(
          uvPos[sourceID * 4 + 0],
          uvPos[sourceID * 4 + 1],
          uvPos[sourceID * 4 + 2],
          uvPos[targetID * 4 + 0],
          uvPos[targetID * 4 + 1],
          uvPos[targetID * 4 + 2]
        );
      }
    }

    avgX /= avgSize;
    avgY /= avgSize;
    avgZ /= avgSize;
    Object.keys(index)
      .sort((a, b) => {
        return a - b;
      })
      .map((id) => {
        let pos = index[id].pos;
        mode1IsNode.push(index[id].isNode);
        mode1Positions.push(pos[0], pos[1], pos[2]);
        return false;
      });

    const textureWidth = 256;
    const textureHeight = 256;

    const dataTexture = new DataTexture(
      uvPos,
      textureWidth,
      textureHeight,
      RGBAFormat,
      FloatType
    );
    dataTexture.needsUpdate = true;
    
    const geometry = new BufferGeometry();
    const lineGeometry = new BufferGeometry();

    geometry.addAttribute("position", new Float32Attribute(positions, 3));
    geometry.addAttribute("textureUV", new Float32Attribute(uv, 2));
    geometry.addAttribute("customColor", new Float32Attribute(colors, 3));
    geometry.addAttribute("size", new Float32Attribute(sizes, 1));
    geometry.addAttribute("myIndex", new Float32Attribute(myIndex, 1));
    geometry.addAttribute(
      "siblingsStart",
      new Float32Attribute(siblingsStart, 1)
    );
    geometry.addAttribute("siblingsEnd", new Float32Attribute(siblingsEnd, 1));
    geometry.addAttribute(
      "mode0PosInit",
      new Float32Attribute(mode0PosInit, 3)
    );
    geometry.addAttribute("mode0Radius", new Float32Attribute(mode0Radius, 1));
    geometry.addAttribute(
      "mode0Centroid",
      new Float32Attribute(mode0Centroid, 3)
    );
    geometry.addAttribute("mode0Speed", new Float32Attribute(mode0Speed, 1));
    geometry.addAttribute(
      "mode0DeltaStart",
      new Float32Attribute(mode0DeltaStart, 1)
    );
    geometry.addAttribute(
      "mode1CommunityID",
      new Float32Attribute(mode1CommunityID, 1)
    );
    geometry.addAttribute(
      "mode1Position",
      new Float32Attribute(mode1Positions, 3)
    );
    geometry.addAttribute("mode1IsNode", new Float32Attribute(mode1IsNode, 1));
    geometry.addAttribute(
      "mode1IsEdgeNode",
      new Float32Attribute(mode1IsEdgeNode, 1)
    );
    geometry.addAttribute("mode1ID", new Float32Attribute(mode1ID, 1));

    lineGeometry.setIndex(new Uint16Attribute(indices, 1));
    lineGeometry.addAttribute(
      "position",
      new Float32Attribute(linepositions, 3)
    );
    lineGeometry.addAttribute(
      "customColor",
      new Float32Attribute(linecolors, 3)
    );
    lineGeometry.addAttribute(
      "displacement",
      new Float32Attribute(linedisplacement, 3)
    );
    lineGeometry.addAttribute(
      "mode0PosInit",
      new Float32Attribute(mode0PosInitLINES, 3)
    );
    lineGeometry.addAttribute(
      "mode2PosInit",
      new Float32Attribute(mode2PosLINES, 3)
    );
    lineGeometry.addAttribute(
      "mode0Radius",
      new Float32Attribute(mode0RadiusLINES, 1)
    );
    lineGeometry.addAttribute(
      "mode0Centroid",
      new Float32Attribute(mode0CentroidLINES, 3)
    );
    lineGeometry.addAttribute(
      "mode0Speed",
      new Float32Attribute(mode0SpeedLINES, 1)
    );
    lineGeometry.addAttribute(
      "mode0DeltaStart",
      new Float32Attribute(mode0DeltaStartLINES, 1)
    );
    lineGeometry.addAttribute("myIndex", new Float32Attribute(myIndexLINES, 1));

    const lineuniforms = {
      amplitude: { value: 5.0 },
      opacity: { value: 0.0 },
      color: { value: new Color(0xffffff) },
      posZ: { value: 1.0 },
      mode: { value: vizMode },
      lerpPercent: { value: 0.0 },
      animationDelta: { value: 0.0 },
      origin: { value: new Vector3(0, 0, 0) },
      direction: { value: new Vector3(0, 0, 0) },
      isInteracting: { value: 0.0 },
    };
    const shaderMaterial = new ShaderMaterial({
      uniforms: lineuniforms,
      vertexShader: lineNetworkVertexShader,
      fragmentShader: lineNetworkFragmentShader,
      blending: AdditiveBlending,
      depthTest: false,
      transparent: true,
    });
    line = new LineSegments(lineGeometry, shaderMaterial);
    scene.add(line);

    const textures = createAnimationsOptimized();
    
    const material = new ShaderMaterial({
      uniforms: {
        color: { value: new Color(0xffffff) },
        pointTexture: { value: new TextureLoader().load('/disc.png') },
        pointTexture2: { value: new TextureLoader().load('/spark1.png') },
        dataTexture: { value: dataTexture },
        sphere0Texture: {value: textures['tl1'].texture},
        sphere1Texture: {value: textures['tl2'].texture},
        sphere2Texture: {value: textures['tl3'].texture},
        sphere0UV: {value: new Vector2(0,0)},
        sphere1UV: {value: new Vector2(0,0)},
        sphere2UV: {value: new Vector2(0,0)},
        sphere0Size: {value: textures['tl1'].size},
        sphere0AnimationDelta: {value: 0},
        sphere0Dimensions: {value: textures['tl1'].dims},
        sphere1Size: {value: textures['tl2'].size},
        sphere1AnimationDelta: {value: 0},
        sphere1Dimensions: {value: textures['tl2'].dims},
        sphere2Size: {value: textures['tl3'].size},
        sphere2AnimationDelta: {value: 0},
        sphere2Dimensions: {value: textures['tl3'].dims},
        animationDelta: { value: 0.0 },
        mode: { value: vizMode },
        lerpPercent: { value: 0.0 },
        origin: { value: new Vector3(0, 0, 0) },
        direction: { value: new Vector3(0, 0, 0) },
        isInteracting: { value: 0.0 },
        time: { value: 0.0 },
        posZ: {value: 0.0}
      },
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,

      blending: AdditiveBlending,
      depthTest: false,
      transparent: true,
    });

    points = new Points(geometry, material);
    scene.add(points);
    points.geometry.computeBoundingBox();
    points.geometry.computeBoundingSphere();
    
    cameraTarget = points.geometry.boundingSphere.center;
    points.position.set(cameraTarget.x, cameraTarget.y, cameraTarget.z);
    line.position.set(cameraTarget.x, cameraTarget.y, cameraTarget.z);
    
    //compute camera positions during the transitions

    const center = new Vector3(
      cameraTarget.x + avgX,
      cameraTarget.y + avgY,
      cameraTarget.z + avgZ
    );
    camera.lookAt(center);
    let topLeftVec3 = new Vector3(
      cameraTarget.x,
      cameraTarget.y + minY,
      cameraTarget.z + minZ
    );
    let bottomRightVec3 = new Vector3(
      cameraTarget.x,
      cameraTarget.y + maxY,
      cameraTarget.z + maxZ
    );
    const boundingBox = new Box3(topLeftVec3, bottomRightVec3);
    const size = boundingBox.size();
    const maxDim = Math.max(size.x, size.y);
    const width = window.innerWidth;
    const height = window.innerHeight;
    let aspectRatio = 1; //size.x > size.y ? width/height : 1;

    if (size.x >= size.y) {
      aspectRatio = Math.min(width / height, size.x / size.y);
    }

    //og camera non-overzoomed
    //let cameraZ = maxDim / 2 / aspectRatio /  Math.tan(Math.PI * camera.fov / 360) ;

    //overzoom to hide the petri dishness :(
    let cameraZ =
      (maxDim /
        2 /
        aspectRatio /
        Math.tan((Math.PI * camera.fov) / 360)) *
      0.25;
    let p0CameraPos = new Vector3(
      30.189275505979424,
      -36.34843941918436,
      7.396685962927879
    );
    let p0CameraTarget = new Vector3(
      44.763425911917054,
      -43.91949108614475,
      -1.6050668637778442
    );

    let p1CameraPos = new Vector3(
      -cameraZ,
      cameraTarget.y + avgY,
      cameraTarget.z + avgZ
    );
    let p1CameraTarget = center;
    let eulerEnd = new Euler(
      -0.8073488662514516,
      -0.9941111577377552,
      -2.5714101088787724
    );
    let panelData = [];
    document.querySelectorAll(".platform-animation .page").forEach((page, id) => {
      const panelD = page.getBoundingClientRect();
      panelData.push({
        height: panelD.height,
        top: panelD.top + window.scrollY,
        id,
      });
    });

    function scrollListener(e) {
      // canvas container to control top position
      const canvasContainer = document.getElementsByClassName('glslRenderer')[0]

      let top = window.scrollY + canvasContainer.getBoundingClientRect().top;

      // Overlapping factor for PanelNumber 2 & 3 
      // Increase or decrease to allow distance from the canvas and the rest of static content
      const overlappingFactor = 0.5;
      let panelNumber = 0;

      let panelCandidate = panelData.find((panelD, i) => {
        if (top >= panelD.top && top < panelD.top + panelD.height) {
          panelNumber = i;
          return { data: panelD, id: panelNumber };
        }
        return false;
      });
      if (top < 0) {
        top = 0;
      }

      if (!panelCandidate) {
        panelCandidate = panelData[0];
      }

      const lerpPercent = Math.min(1, (window.scrollY - panelCandidate.top) / panelCandidate.height);
      points.material.uniforms.mode.value = panelNumber;
      points.material.uniforms.lerpPercent.value = lerpPercent;
      line.material.uniforms.mode.value = panelNumber;
      line.material.uniforms.lerpPercent.value = lerpPercent;

      if (panelNumber < 1.0) {
        let pos = p0CameraPos.clone().lerp(p1CameraPos.clone(), lerpPercent);
        let c = p0CameraTarget
          .clone()
          .lerp(p1CameraTarget.clone(), lerpPercent);
        camera.position.set(pos.x, pos.y, pos.z);
        camera.lookAt(c);
        topoLines = scene.children.find((d) => {
          return d.name === "contours";
        });
        if (typeof topoLines !== "undefined") {
          if (lerpPercent >= 0.5) {
            topoLines.material.opacity = 0
            topoLines.material.uniforms.mode.value = 0.0;
            topoLines.material.uniforms.posZ.value = 0.0;
          }
        }
        line.material.uniforms.opacity.value = lerpPercent;

        /////////////////// NEW APPROACH HERE ///////////////////
        objLines = scene.children.find((d) => {
          return d.name === "objcontours";
        });
        if (typeof objLines !== "undefined") {
          objLines.children[0].material.uniforms.globalOpacity.value = 0
          objLines.children[0].material.uniforms.posZ.value = 0.15
          points.material.uniforms.posZ.value = 0;
        }
        performHeightAnimation = false;
      }

      if (panelNumber >= 1.0 && panelNumber < 2.0) {
        performHeightAnimation = false;
        let pointsPos = cameraTarget
          .clone()
          .lerp(new Vector3(0, 0, 0), lerpPercent);
        points.position.set(pointsPos.x, pointsPos.y, pointsPos.z);



        let pos = p1CameraPos.clone().lerp(p0CameraPos.clone(), lerpPercent);
        let c = p1CameraTarget
          .clone()
          .lerp(p0CameraTarget.clone(), lerpPercent);
        camera.position.set(pos.x, pos.y, pos.z);
        camera.lookAt(c);
        
        const quaternionEnd = new Quaternion().setFromEuler(eulerEnd);
        camera.quaternion.slerp(quaternionEnd,lerpPercent);
        //use quaternion instead of euler for this lerp to eliminate that bit of gimbal snap at the start of mode 1 :facepalm:

        topoLines = scene.children.find((d) => {
          return d.name === "contours";
        });
        if (typeof topoLines !== "undefined") {
          if (lerpPercent >= 0.5) {
            topoLines.material.opacity = 1
            topoLines.material.uniforms.mode.value = 2.0;
            topoLines.material.uniforms.posZ.value = lerpPercent;
          }
        }
        let lineVal = (1.0 - (lerpPercent*3));
        lineVal = lineVal < 0 ? 0 : lineVal;
        //make the lines fade out a bit faster
        line.material.uniforms.opacity.value = lineVal;

        /////////////////// NEW APPROACH HERE ///////////////////
        objLines = scene.children.find((d) => {
          return d.name === "objcontours";
        });
        if (typeof objLines !== "undefined") {
          if (lerpPercent >= 0.1) {
            objLines.children[0].material.uniforms.globalOpacity.value = lerpPercent*0.5;
          }
        }

        if(lerpPercent >= 0.2){
          performHeightAnimation = true;
        }
      }
      if (panelNumber >= 2.0  && panelNumber < 3.0) {

        performHeightAnimation = true;

        objLines = scene.children.find((d) => {
          return d.name === "objcontours";
        });
        if (typeof objLines !== "undefined") {
          if (lerpPercent >= 0.5) {           
            objLines.position.x = 150
            objLines.position.y = -105
          }
          
        }

        //keep at contours if we go above 3 pages
        panelNumber = 2.0;
        let overlappingLerpPercent = lerpPercent 
        overlappingLerpPercent += overlappingFactor
        overlappingLerpPercent = (overlappingLerpPercent > 1) ? 1 : overlappingLerpPercent 
        
        points.material.uniforms.mode.value = panelNumber;
        points.material.uniforms.lerpPercent.value = overlappingLerpPercent ;
        camera.lookAt(p0CameraTarget);
        camera.position.set(p0CameraPos.x, p0CameraPos.y, p0CameraPos.z);
        camera.setRotationFromEuler(
          new Euler(
            -0.8073488662514516,
            -0.9941111577377552,
            -2.5714101088787724
          )
        );
        points.position.set(0, 0, 0);
        topoLines = scene.children.find((d) => {
          return d.name === "contours";
        });
        if (typeof topoLines !== "undefined") {
        }
        line.material.uniforms.opacity.value = 0;
        // if (lerpPercent > overlappingFactor) {
        //   const distanceTop = (lerpPercent - overlappingFactor) * window.innerHeight;
        //   canvasContainer.style.top = `-${distanceTop}px`
        // } else {
        //   canvasContainer.style.top = `0px`
        // }
      }

      if (panelNumber === 3) {
        performHeightAnimation = true;
        camera.lookAt(p0CameraTarget);
        camera.position.set(p0CameraPos.x, p0CameraPos.y, p0CameraPos.z);
        camera.setRotationFromEuler(
          new Euler(
            -0.8073488662514516,
            -0.9941111577377552,
            -2.5714101088787724
          )
        );
        // const distanceTop = (lerpPercent + overlappingFactor) * window.innerHeight;
        // canvasContainer.style.top = `-${distanceTop}px`;x`

        points.material.uniforms.mode.value = 2;
        points.material.uniforms.lerpPercent.value = 1.0; 
        line.material.uniforms.opacity.value = 0;
      }
      vizMode = panelNumber;
    }
    scrollListener();
    document.addEventListener("scroll", scrollListener);
    if (showStats) {
      stats = Stats();
      document.body.appendChild(stats.dom);
    }
    document.addEventListener("mousemove", onMouseMove, false);
    window.addEventListener("resize", onWindowResize, false);
    window.addEventListener("orientationchange", onWindowResize, false);

    function onWindowResize() {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();

      renderer.setSize(window.innerWidth, window.innerHeight);
    }
    function onMouseMove(event) {
      mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
      //set the ray from the mouse out into the camera/3d-world
      raycaster.setFromCamera(mouse, camera);

      inverseMatrix.getInverse(points.matrixWorld);
      ray.copy(raycaster.ray).applyMatrix4(inverseMatrix);

      points.material.uniforms.origin.value = ray.origin;
      points.material.uniforms.direction.value = ray.direction;
      points.material.uniforms.isInteracting.value = 1.0;
      line.material.uniforms.origin.value = ray.origin;
      line.material.uniforms.direction.value = ray.direction;
      line.material.uniforms.isInteracting.value = 1.0;
    }

    let startTime = Date.now();

    function animate() {
      animationFrameStep = requestAnimationFrame(animate);
      if (points) {
        let currentTime = Date.now();
        let elapsedTime = (currentTime - startTime) / 1000.0; // Convert to seconds
        startTime = currentTime;
        points.material.uniforms.time.value += elapsedTime;
        points.material.uniforms.animationDelta.value += 0.25;
        line.material.uniforms.animationDelta.value += 0.25;
        
        //update an animation frame int for the points gpgpu shader to pick up in order to calculate uv for the data texture to get sphere positions
        const sphere0Size = points.material.uniforms.sphere0Size.value;
        points.material.uniforms.sphere0AnimationDelta.value = points.material.uniforms.sphere0AnimationDelta.value + 1 >= sphere0Size ? 0 : points.material.uniforms.sphere0AnimationDelta.value + 1;
        
        const sphere1Size = points.material.uniforms.sphere1Size.value;
        points.material.uniforms.sphere1AnimationDelta.value = points.material.uniforms.sphere1AnimationDelta.value + 1 >= sphere1Size ? 0 : points.material.uniforms.sphere1AnimationDelta.value + 1;
        
        const sphere2Size = points.material.uniforms.sphere2Size.value;
        points.material.uniforms.sphere2AnimationDelta.value = points.material.uniforms.sphere2AnimationDelta.value + 1 >= sphere2Size ? 0 : points.material.uniforms.sphere2AnimationDelta.value + 1;
        

        if(performHeightAnimation){
          //animate in the height of the terrain + opacity
          if (typeof objLines !== "undefined") {
            objLines.children[0].material.uniforms.posZ.value = objLines.children[0].material.uniforms.posZ.value < 1.0 ? objLines.children[0].material.uniforms.posZ.value + 0.05 : objLines.children[0].material.uniforms.posZ.value;
            objLines.children[0].material.uniforms.globalOpacity.value = objLines.children[0].material.uniforms.globalOpacity.value < 1.0 ? objLines.children[0].material.uniforms.globalOpacity.value + 0.05 : objLines.children[0].material.uniforms.globalOpacity.value;
            if(objLines.children[0].material.uniforms.posZ.value >= 1.0){
              performHeightAnimation = false;
              objLines.children[0].material.uniforms.posZ.value = 1.0;
              objLines.children[0].material.uniforms.posZ.value = 1.0;
            }
          }
        }
      }
      renderer.render(scene, camera);
      if (showStats) {
        stats.update();
      }
    }

    animate();



    return () => {
      //component re-mounted, remove things
      if (animationFrameStep) {
        cancelAnimationFrame(animationFrameStep);
      }

      window.removeEventListener("resize", onWindowResize);
      window.removeEventListener("orientationchange", onWindowResize);
      //document.removeEventListener('keydown',keydownlistener);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("scroll", scrollListener);
      scene.remove(points);
      scene.remove(line);
    };
  }, [props]);
  return <Pages />;
}

export default ContourPoints;
